import type { Style, TextAlign } from "@bezpart-board/shared";

export type TextToolSettings = {
  color: string;
  fontSize: number;
  fontFamily: string;
  textAlign: TextAlign;
  fontWeight: "normal" | "bold";
  fontStyle: "normal" | "italic";
  textDecoration: string;
};

const STORAGE_KEY = "wb-text-settings";

/**
 * Шрифт надписей — один на всех устройствах.
 *
 * Раньше надписи рисовались системным шрифтом (system-ui): на Windows это
 * Segoe UI, на Mac и iPad — San Francisco, на Android — Roboto. Буквы у них
 * разной ширины, и строка, которая у учителя влезала, у ученика переносилась:
 * всё ниже съезжало на строку, и пометки пером ложились не туда. Onest —
 * фирменный шрифт сайта с кириллицей, он и так грузится со страницей.
 */
export const BOARD_TEXT_FONT = '"Onest", system-ui, sans-serif';

/** Что стояло у надписей раньше — читаем как «шрифт доски». */
const LEGACY_TEXT_FONT = "system-ui, sans-serif";

/** Шрифт надписи для показа и измерения: старое значение — шрифт доски. */
export function normalizeTextFont(fontFamily: string | null | undefined): string {
  if (!fontFamily || fontFamily.trim() === LEGACY_TEXT_FONT) return BOARD_TEXT_FONT;
  return fontFamily;
}

const DEFAULT_SETTINGS: TextToolSettings = {
  color: "#1a1a2e",
  fontSize: 18,
  fontFamily: BOARD_TEXT_FONT,
  textAlign: "left",
  fontWeight: "normal",
  fontStyle: "normal",
  textDecoration: "none",
};

function loadSettings(): TextToolSettings {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const p = JSON.parse(raw) as Partial<TextToolSettings>;
    return {
      color: typeof p.color === "string" ? p.color : DEFAULT_SETTINGS.color,
      fontSize:
        typeof p.fontSize === "number" && Number.isFinite(p.fontSize)
          ? p.fontSize
          : DEFAULT_SETTINGS.fontSize,
      fontFamily:
        typeof p.fontFamily === "string"
          ? normalizeTextFont(p.fontFamily)
          : DEFAULT_SETTINGS.fontFamily,
      textAlign:
        p.textAlign === "left" || p.textAlign === "center" || p.textAlign === "right"
          ? p.textAlign
          : DEFAULT_SETTINGS.textAlign,
      fontWeight: p.fontWeight === "bold" ? "bold" : "normal",
      fontStyle: p.fontStyle === "italic" ? "italic" : "normal",
      textDecoration:
        typeof p.textDecoration === "string" && p.textDecoration !== "none"
          ? p.textDecoration
          : "none",
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

let settings: TextToolSettings = loadSettings();

export function getTextSettings(): TextToolSettings {
  return settings;
}

export function setTextSettings(partial: Partial<TextToolSettings>): void {
  settings = { ...settings, ...partial };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}

export function textStyleFields(s: TextToolSettings): Style {
  return {
    color: s.color,
    fontSize: s.fontSize,
    fontFamily: s.fontFamily,
    textAlign: s.textAlign,
    fontWeight: s.fontWeight,
    fontStyle: s.fontStyle,
    textDecoration: s.textDecoration === "none" ? undefined : s.textDecoration,
    lineHeight: 1.35,
  };
}

export const TEXT_COLOR_PRESETS = [
  "#1a1a2e",
  "#0f172a",
  "#dc2626",
  "#2563eb",
  "#16a34a",
  "#ffffff",
] as const;

/**
 * Размеры шрифта для выбора в панели. Список длинный — в панели он показан
 * выпадающим списком, а кнопками «−/+» можно шагать по соседним значениям.
 */
export const TEXT_SIZE_PRESETS = [
  8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96, 128,
] as const;

/** Границы размера шрифта — те же, что при растягивании за угол. */
export const TEXT_SIZE_MIN = 8;
export const TEXT_SIZE_MAX = 300;
